//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

// The rail's layout arithmetic -- which pills rank for the visible slots, and
// how many of those slots there are -- kept pure and separate from the
// component that renders it: jsdom reports every measured width as zero, so
// the ladder is only testable where the widths are arguments rather than
// measurements.

export interface PillFit {
  // How many leading pills are drawn, in the order given.
  readonly visible: number;
  // How many are behind the `+N` flyout. Zero means no flyout is drawn.
  readonly overflow: number;
}

/**
 * How many of `widths` fit in `available`, reserving `overflowWidth` for the
 * `+N` control as soon as anything has to hide.
 *
 * The reservation happens before the fitting, not after: a control sized
 * afterwards would have nowhere to sit, and the rail's row is
 * `overflow: hidden`, so it would vanish silently along with the pills it
 * was meant to reveal.
 */
const fitPills = (
  widths: number[], available: number, overflowWidth: number
): PillFit => {
  if (widths.length === 0) return { visible: 0, overflow: 0 };

  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total <= available) {
    return { visible: widths.length, overflow: 0 };
  }

  const room = available - overflowWidth;
  let used = 0;
  let visible = 0;
  for (const width of widths) {
    if (used + width > room) break;
    used += width;
    visible += 1;
  }
  return { visible, overflow: widths.length - visible };
};

// A gap sits between pills, so n pills are charged n-1 gaps: the first is
// charged none. An extra gap here is not a rounding error but a squeeze at
// every viewport there is, since the charge is compared against the slot at
// each one.
//
// Applied inside `resolveRailFit`, after the measured check, and not by the
// caller: a gap added first turns an unmeasured zero into a positive width,
// and the check that reads zero as "not yet measured" would see a pill it
// believes is 4px wide.
const withGaps = (widths: number[], gap: number): number[] =>
  widths.map((width, index) => index === 0 ? width : width + gap);

/**
 * The rail's degradation: full pills, then full pills beside a `+N` flyout
 * holding the surplus.
 *
 * There is deliberately no icon-only tier. Collapsing a pill to its glyph
 * keeps it on the rail at the cost of its name, which is the half a
 * first-time user actually reads -- an unlabeled glyph is not a smaller
 * affordance but a guess, and the guess is worst for the people who most
 * need the label. A pill in the flyout is a labeled row one click away,
 * which is a better outcome than a row of anonymous glyphs.
 *
 * `widths` are the pills as measured, before any gap is charged: a zero is an
 * absence of measurement, not an absence of room -- the same reading
 * `panelGeometry` gives an unmeasured bounds axis. The rail measures the
 * pills it renders, so collapsing on an unmeasured width would hide the pills
 * whose widths the next measurement needs. The gaps are added below, once
 * that reading has been taken.
 *
 * A zero `available` is the opposite: it is a room of no width, which the
 * rail's slot genuinely reports when the header has nothing left to give.
 * Not-yet-measured is `null`, so the two cannot be confused -- reading a
 * real zero as "unmeasured" would draw every pill into a slot that cannot
 * hold one, and the `+N` that was meant to reach them would be clipped away
 * with them.
 */
export const resolveRailFit = (
  widths: number[], available: number | null, overflowWidth: number,
  gap: number
): PillFit => {
  const isMeasured = available !== null && widths.every((width) => width > 0);
  if (!isMeasured) return { visible: widths.length, overflow: 0 };
  return fitPills(withGaps(widths, gap), available, overflowWidth);
};
export interface RankableTool {
  readonly id: string;
  // Mounted: on screen or parked, either way holding live state.
  readonly isRunning: boolean;
  readonly isPinned: boolean;
}

// Running first, then pinned, then everything else. A running tool leads
// whether or not it is pinned: it is holding state, and it is the one a user
// is most likely to be reaching back for.
const TOOL_RANK = (tool: RankableTool): number => {
  if (tool.isRunning) return 0;
  if (tool.isPinned) return 1;
  return 2;
};

/**
 * Order the taskbar, most deserving of a visible slot first.
 *
 * Stable inside each tier, so nothing shuffles for a reason the user cannot
 * see. The tiers themselves do move under them -- opening a tool promotes it
 * past the pinned ones, which shifts those right. That movement follows the
 * user's own action rather than arriving unbidden, which is the trade this
 * ordering makes against a pinned slot that never moves at all.
 */
export const orderTools = <T extends RankableTool>(tools: T[]): T[] =>
  tools.toSorted((a, b) => TOOL_RANK(a) - TOOL_RANK(b));
